/**
 * The clinic's theme: one colour in, two numbers out. Shared by the client
 * (which puts the numbers on <html>) and the server (which writes the same
 * colours inline into exported articles - see articlePalette below).
 *
 * styles.css derives every brand colour from `--brand-h` and `--brand-c` (hue
 * and chroma in OKLCH) at fixed lightness steps - see the note at its :root.
 * This file turns the `#rrggbb` somebody picked into those two numbers, puts
 * them on <html>, and remembers them for the next page load. That is all a
 * theme change is: two custom properties and one style recalculation. No
 * palette is computed here, and no component knows a theme exists.
 *
 * No dependency: the conversion is Björn Ottosson's published OKLab matrices,
 * about twenty lines.
 */

/** The product's own colour - what a clinic with no theme is drawn in. */
export const DEFAULT_BRAND = '#e0417c';

/** The seed's lightness in styles.css (--brand-600): where chroma is measured. */
const SEED_L = 0.621;
/** Past this a seed stops reading as a brand and starts reading as a warning. */
const MAX_CHROMA = 0.26;
export const HEX = /^#[0-9a-f]{6}$/i;

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);

/** `#rrggbb` → OKLCH. */
export function oklchOf(hex: string): { l: number; c: number; h: number } {
  const n = hex.slice(1);
  const [r, g, b] = [0, 2, 4].map((i) => toLinear(parseInt(n.slice(i, i + 2), 16) / 255)) as [
    number,
    number,
    number,
  ];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const h = (Math.atan2(B, A) * 180) / Math.PI;
  return { l: L, c: Math.hypot(A, B), h: h < 0 ? h + 360 : h };
}

/** OKLCH → linear sRGB, unclamped - out-of-range channels mean out of gamut. */
function linearRgb(l: number, c: number, h: number): [number, number, number] {
  const a = c * Math.cos((h * Math.PI) / 180);
  const b = c * Math.sin((h * Math.PI) / 180);
  const L = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const M = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const S = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * L - 3.3077115913 * M + 0.2309699292 * S,
    -1.2684380046 * L + 2.6097574011 * M - 0.3413193965 * S,
    -0.0041960863 * L - 0.7034186147 * M + 1.707614701 * S,
  ];
}

const inGamut = (l: number, c: number, h: number) =>
  linearRgb(l, c, h).every((channel) => channel >= -0.0005 && channel <= 1.0005);

/**
 * The two numbers for a picked colour.
 *
 * Chroma is capped at what the screen can actually show at the seed's
 * lightness for that hue. A browser shown an out-of-gamut OKLCH colour clips it
 * channel by channel, which shifts the HUE - a vivid cyan comes out green. So
 * the cap is found here, by bisection, and the scale is built inside it.
 *
 * ponytail: measured at the seed step only. The darkest steps of a very light
 * hue (yellow at L 0.36) can still sit a little outside sRGB and get clipped;
 * per-step caps would mean moving the scale into JavaScript, which is the one
 * thing this design avoids.
 */
export function seedOf(hex: string): { h: number; c: number } {
  const { c, h } = oklchOf(hex);
  let lo = 0;
  let hi = Math.min(c, MAX_CHROMA);
  if (!inGamut(SEED_L, hi, h)) {
    for (let i = 0; i < 16; i += 1) {
      const mid = (lo + hi) / 2;
      if (inGamut(SEED_L, mid, h)) lo = mid;
      else hi = mid;
    }
    hi = lo;
  }
  return { h: Math.round(h * 10) / 10, c: Math.round(hi * 1000) / 1000 };
}

/** WCAG relative luminance, from linear sRGB. */
const luminance = ([r, g, b]: [number, number, number]) =>
  0.2126 * Math.min(1, Math.max(0, r)) +
  0.7152 * Math.min(1, Math.max(0, g)) +
  0.0722 * Math.min(1, Math.max(0, b));

/**
 * Contrast of white text on this theme's primary colour (--brand-600), as the
 * WCAG ratio. The picker shows it so nobody has to take "readable regardless"
 * on trust. Fixed lightness keeps it near 3.9:1 for every hue - the product's
 * buttons are bold and large enough that WCAG's 3:1 for large text applies.
 */
export function contrastOnBrand(hex: string): number {
  const { h, c } = seedOf(hex);
  const lum = luminance(linearRgb(SEED_L, c, h));
  return Math.round(((1 + 0.05) / (lum + 0.05)) * 10) / 10;
}

/** The theme's primary colour as CSS, for places that need a literal colour. */
export const primaryCss = (hex: string) => {
  const { h, c } = seedOf(hex);
  return `oklch(${SEED_L} ${c} ${h})`;
};

/** Set (or, with null, clear) the theme on an element - <html> by default. */
export function themeStyle(hex: string | null): Record<string, string> {
  if (!hex || !HEX.test(hex)) return {};
  const { h, c } = seedOf(hex);
  return { '--brand-h': String(h), '--brand-c': String(c) };
}

/** Presets: distinct hues, all of which make a good theme at these steps. */
export const THEME_PRESETS = [
  { hex: '#e0417c', label: 'サクラ（既定）' },
  { hex: '#c2410c', label: 'テラコッタ' },
  { hex: '#b45309', label: 'アンバー' },
  { hex: '#15803d', label: 'グリーン' },
  { hex: '#0f766e', label: 'ティール' },
  { hex: '#0369a1', label: 'オーシャン' },
  { hex: '#4338ca', label: 'インディゴ' },
  { hex: '#7e22ce', label: 'パープル' },
  { hex: '#be185d', label: 'ローズ' },
  { hex: '#57534e', label: 'グレージュ' },
];

// --------------------------------------------------------------- articles

/**
 * The theme as concrete colours, for places a stylesheet cannot reach.
 *
 * An exported article is pasted into WordPress or opened as a file, where our
 * CSS does not exist, so its CTA button, supervisor card and contents box carry
 * their colours INLINE. Those used to be hard-coded sakura pink whatever the
 * clinic's colour; this is the same scale styles.css derives (the steps below
 * mirror its :root - keep the two in step), resolved to hex on the server.
 */
export interface ArticlePalette {
  brand800: string;
  brand700: string;
  brand600: string;
  brand300: string;
  brand100: string;
  brand050: string;
  line: string;
  lineStrong: string;
  ink900: string;
  ink700: string;
  ink500: string;
  /** Faint panel tints and hairlines: tables, the contents box, the page. */
  tint: string;
  panel: string;
  border: string;
  avatarBorder: string;
  hairline: string;
  pageBg: string;
  inkMuted: string;
  inkBody: string;
}

/**
 * The original, exact values - what an export has always written. A clinic
 * with no colour exports byte-for-byte what it did before themes existed.
 */
export const DEFAULT_ARTICLE_PALETTE: ArticlePalette = {
  brand800: '#96214d',
  brand700: '#b4386f',
  brand600: '#e0417c',
  brand300: '#f8a5c4',
  brand100: '#fde3ee',
  brand050: '#fff5f9',
  line: '#f0d3e2',
  lineStrong: '#e8bed2',
  ink900: '#2b2130',
  ink700: '#4d4453',
  ink500: '#7b7180',
  tint: '#faf5f8',
  panel: '#fdf6fa',
  border: '#e3dbe0',
  avatarBorder: '#f7cfe1',
  hairline: '#eadfe5',
  pageBg: '#fbf9fa',
  inkMuted: '#6b5f68',
  inkBody: '#2b2430',
};

/** [lightness, chroma ratio, hue offset] per step - styles.css :root. */
const STEPS: Record<keyof ArticlePalette, [number, number, number]> = {
  brand800: [0.453, 0.779, 0],
  brand700: [0.534, 0.874, 0],
  brand600: [0.621, 1, 0],
  brand300: [0.812, 0.528, -7],
  brand100: [0.94, 0.161, -12],
  brand050: [0.979, 0.06, -11.5],
  line: [0.895, 0.191, -17.2],
  lineStrong: [0.844, 0.271, -14.5],
  ink900: [0.266, 0.156, -48],
  ink700: [0.401, 0.136, -50.5],
  ink500: [0.563, 0.131, -48],
  tint: [0.975, 0.035, -22.1],
  panel: [0.98, 0.045, -20.7],
  border: [0.899, 0.055, -23.1],
  avatarBorder: [0.894, 0.251, -13.2],
  hairline: [0.914, 0.07, -19.3],
  pageBg: [0.984, 0.01, -17.3],
  inkMuted: [0.5, 0.106, -27.9],
  inkBody: [0.273, 0.121, -51.5],
};

const encode = (v: number) => {
  const c = Math.min(1, Math.max(0, v));
  const s = c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
  return Math.round(s * 255).toString(16).padStart(2, '0');
};

/** A clinic's colour (or null for the default) as the article palette. */
export function articlePalette(hex: string | null | undefined): ArticlePalette {
  if (!hex || !HEX.test(hex) || hex.toLowerCase() === DEFAULT_BRAND) return DEFAULT_ARTICLE_PALETTE;
  const { h, c } = seedOf(hex);
  const out = {} as ArticlePalette;
  for (const [key, [l, ratio, offset]] of Object.entries(STEPS) as Array<
    [keyof ArticlePalette, [number, number, number]]
  >) {
    const [r, g, b] = linearRgb(l, c * ratio, h + offset);
    out[key] = `#${encode(r)}${encode(g)}${encode(b)}`;
  }
  return out;
}

// --------------------------------------------------------------- one block

/**
 * The colours of ONE article block - a CTA, a supervisor card - from the one
 * colour a writer picked for it (the block toolbar's swatch).
 *
 * Unlike the theme, the pick is used EXACTLY for the button: a clinic choosing
 * its booking button's colour means that colour, usually off its own website.
 * Everything else follows from it so the block cannot be made unreadable:
 *
 *   buttonInk   white or near-black, whichever reads better on the button
 *   title       the pick darkened until it clears WCAG AA (4.5:1) on the tint
 *   tint/chip/border  the pick mixed towards white
 *
 * The editor (as CSS variables, Callout.ts) and the export (inline, lib/
 * markdown.ts) both call this, so a block looks the same in both.
 */
export interface BlockColors {
  button: string;
  buttonHover: string;
  buttonInk: string;
  title: string;
  tint: string;
  chip: string;
  border: string;
}

const channels = (hex: string) =>
  [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];

const hexOf = ([r, g, b]: [number, number, number]) =>
  `#${[r, g, b].map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('')}`;

/** `a` moved `t` of the way towards `b`, in sRGB - what "mix with white" means. */
const mix = (a: string, b: string, t: number) => {
  const [x, y] = [channels(a), channels(b)];
  return hexOf([0, 1, 2].map((i) => x[i]! + (y[i]! - x[i]!) * t) as [number, number, number]);
};

/** WCAG relative luminance and contrast ratio. */
const lum = (hex: string) =>
  channels(hex)
    .map((v) => toLinear(v / 255))
    .reduce((sum, v, i) => sum + v * [0.2126, 0.7152, 0.0722][i]!, 0);
export const contrastRatio = (a: string, b: string) => {
  const [x, y] = [lum(a), lum(b)].sort((m, n) => n - m) as [number, number];
  return (x + 0.05) / (y + 0.05);
};

const INK = '#2b2130';

export function blockColors(hex: string): BlockColors {
  const pick = hex.toLowerCase();
  const tint = mix(pick, '#ffffff', 0.92);
  let title = mix(pick, '#000000', 0.1);
  for (let i = 0; i < 10 && contrastRatio(title, tint) < 4.5; i += 1) title = mix(title, '#000000', 0.15);
  return {
    button: pick,
    buttonHover: mix(pick, '#000000', 0.12),
    buttonInk: contrastRatio(pick, '#ffffff') >= contrastRatio(pick, INK) ? '#ffffff' : INK,
    title,
    tint,
    chip: mix(pick, '#ffffff', 0.82),
    border: mix(pick, '#ffffff', 0.55),
  };
}

/** The same colours as CSS custom properties, for the editor's block element. */
export function blockStyle(hex: string): string {
  const c = blockColors(hex);
  return (
    `--block: ${hex.toLowerCase()}; --block-button: ${c.button}; --block-button-hover: ${c.buttonHover}; ` +
    `--block-ink: ${c.buttonInk}; --block-title: ${c.title}; --block-tint: ${c.tint}; ` +
    `--block-chip: ${c.chip}; --block-border: ${c.border}`
  );
}
